import { Box } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ForbiddenError,
    type MFAMethod,
    TooManyRequestsError,
    UnauthorizedError,
} from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { Alert } from 'src/lib/components/Alert';
import { Button } from 'src/lib/components/Button';
import { TextField } from 'src/lib/components/fields/TextField';
import { Typography } from 'src/lib/components/Typography';
import { buildAuthInstrumentationHeaders } from '../authInstrumentationHeaders';
import {
    PasskeyBrowserError,
    isPasskeySupported,
    startPasskeyAuthentication,
} from '../passkeyBrowser';
import {
    buildMfaChallengeVerifyRequest,
    getInitialMfaMethod,
    getUsableChallengeMethods,
    type PendingMFAChallenge,
} from './loginMfaChallenge';

export type MfaChallengeRetryResult =
    | { outcome: 'challenge'; challenge: PendingMFAChallenge }
    | { outcome: 'verified' }
    | { outcome: 'failed' };

interface Props {
    challenge: PendingMFAChallenge;
    /** Called after the challenge verifies and the session cookies are set. */
    onVerified(): void | Promise<void>;
    /**
     * A failed verification consumes the challenge token. Re-run the original
     * login request to mint a fresh challenge; on 'challenge' the parent must
     * also update the `challenge` prop, and on 'failed' the parent owns any
     * error state (this form is expected to unmount).
     */
    onRetryChallenge(): Promise<MfaChallengeRetryResult>;
    /** Defaults to showing the error inline so the user can retry. */
    onUnexpectedError?(error: unknown): void;
    onBack?(): void;
}

const methodSwitchLabelKey = (method: MFAMethod) => {
    if (method === 'sms') return 'mfaUseSmsCode';
    if (method === 'recovery_code') return 'mfaUseRecoveryCode';
    if (method === 'passkey') return 'mfaUsePasskey';
    return 'mfaUseAuthenticatorCode';
};

export const MfaChallengeForm = ({
    challenge,
    onVerified,
    onRetryChallenge,
    onUnexpectedError,
    onBack,
}: Props) => {
    const { t } = useTranslation('auth.login');
    const { t: tValidation } = useTranslation('validation');
    const api = useAPI();

    const usableMethods = getUsableChallengeMethods(challenge.methods, isPasskeySupported());
    const [selectedMethod, setSelectedMethod] = useState<MFAMethod | null>(null);
    const [mfaCode, setMfaCode] = useState('');
    const [mfaError, setMfaError] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);

    const mfaMethod =
        selectedMethod && usableMethods.includes(selectedMethod)
            ? selectedMethod
            : getInitialMfaMethod(usableMethods);

    const handleUnexpectedError = (err: any) => {
        if (onUnexpectedError) {
            onUnexpectedError(err);
            return;
        }
        setMfaError(err?.message || t('mfaVerificationFailed'));
    };

    const verifyChallenge = async (body: { method: MFAMethod } & Record<string, unknown>) => {
        setIsVerifying(true);
        setMfaError(null);
        try {
            await api.post(
                '/auth/mfa/challenge/verify/',
                body,
                undefined,
                buildAuthInstrumentationHeaders({
                    flow: 'mfa_challenge',
                    includeResetCorrelationId: true,
                }),
            );
            await onVerified();
        } catch (err: any) {
            if (err instanceof UnauthorizedError || err instanceof ForbiddenError) {
                const retry = await onRetryChallenge();
                if (retry.outcome === 'challenge') {
                    setMfaCode('');
                    setMfaError(
                        body.method === 'passkey'
                            ? t('mfaVerificationFailed')
                            : t('mfaInvalidCode'),
                    );
                }
                return;
            }
            if (err instanceof TooManyRequestsError) {
                setMfaError(err.message || t('mfaTooManyAttempts'));
                return;
            }
            handleUnexpectedError(err);
        } finally {
            setIsVerifying(false);
        }
    };

    const verifyWithPasskey = async () => {
        setIsVerifying(true);
        setMfaError(null);
        let credential: Record<string, unknown>;
        try {
            const { options } = await api.post(
                '/auth/passkeys/authentication/options/',
                {},
                undefined,
                buildAuthInstrumentationHeaders({
                    flow: 'mfa_challenge',
                    includeResetCorrelationId: true,
                }),
            );
            credential = await startPasskeyAuthentication(options);
        } catch (err: any) {
            setIsVerifying(false);
            if (err instanceof PasskeyBrowserError) {
                setMfaError(
                    t(`passkeyBrowser.${err.operation}.${err.errorCode}`, {
                        defaultValue: t('mfaVerificationFailed'),
                    }),
                );
                return;
            }
            handleUnexpectedError(err);
            return;
        }
        // The challenge token is only consumed once the credential reaches the
        // verify endpoint, so a canceled browser prompt above keeps it valid.
        await verifyChallenge(
            buildMfaChallengeVerifyRequest({
                challenge,
                code: '',
                method: 'passkey',
                passkeyCredential: credential,
            }),
        );
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mfaMethod === 'passkey') {
            await verifyWithPasskey();
            return;
        }
        if (!mfaCode.trim()) {
            setMfaError(tValidation('required'));
            return;
        }
        await verifyChallenge(
            buildMfaChallengeVerifyRequest({
                challenge,
                code: mfaCode,
                method: mfaMethod,
            }),
        );
    };

    const isPasskey = mfaMethod === 'passkey';
    const isRecoveryCode = mfaMethod === 'recovery_code';
    const isSms = mfaMethod === 'sms';
    const methodSwitchOptions = usableMethods
        .filter((method) => method !== mfaMethod)
        .map((method) => ({ method, label: t(methodSwitchLabelKey(method)) }));

    if (usableMethods.length === 0) {
        // Passkey-only challenge in a browser without WebAuthn support: a code
        // field could never verify, so say why instead of dead-ending.
        return (
            <Box>
                <Box marginBottom={3}>
                    <Typography role="heading2" component="h1">
                        {t('mfaTitle')}
                    </Typography>
                </Box>
                <Box marginBottom={4}>
                    <Alert variant="critical">
                        {t('passkeyBrowser.authentication.unsupported')}
                    </Alert>
                </Box>
                {onBack && (
                    <Button
                        type="button"
                        kind="outlined"
                        color="secondary"
                        fullWidth
                        onClick={onBack}
                    >
                        {t('mfaBackToLogin')}
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit} autoComplete="on">
            <Box marginBottom={3}>
                <Typography role="heading2" component="h1">
                    {t('mfaTitle')}
                </Typography>
            </Box>
            <Box marginBottom={3}>
                <Typography role="body" color="subdued">
                    {isPasskey
                        ? t('mfaPasskeyDescription', { username: challenge.username })
                        : isSms
                          ? t('mfaSmsDescription', { username: challenge.username })
                          : t('mfaDescription', { username: challenge.username })}
                </Typography>
            </Box>
            {isPasskey ? (
                mfaError && (
                    <Box marginBottom={4}>
                        <Alert variant="critical">{mfaError}</Alert>
                    </Box>
                )
            ) : (
                <Box marginBottom={4}>
                    <TextField
                        label={
                            isRecoveryCode
                                ? t('mfaRecoveryCodeLabel')
                                : isSms
                                  ? t('mfaSmsCodeLabel')
                                  : t('mfaCodeLabel')
                        }
                        name="mfa_code"
                        autoComplete="one-time-code"
                        value={mfaCode}
                        onChange={(event) => {
                            setMfaCode(event.target.value);
                            setMfaError(null);
                        }}
                        error={mfaError ?? undefined}
                        autoFocus
                    />
                </Box>
            )}
            <Box marginBottom={2}>
                <Button
                    type="submit"
                    kind="filled"
                    color="primary"
                    fullWidth
                    disabled={isVerifying}
                >
                    {isPasskey ? t('mfaVerifyWithPasskey') : t('mfaVerifyButton')}
                </Button>
            </Box>
            {methodSwitchOptions.map((option) => (
                <Box key={option.method} marginBottom={2}>
                    <Button
                        type="button"
                        kind="text"
                        color="secondary"
                        fullWidth
                        disabled={isVerifying}
                        onClick={() => {
                            setSelectedMethod(option.method);
                            setMfaCode('');
                            setMfaError(null);
                        }}
                    >
                        {option.label}
                    </Button>
                </Box>
            ))}
            {onBack && (
                <Box marginBottom={2}>
                    <Button
                        type="button"
                        kind="outlined"
                        color="secondary"
                        fullWidth
                        disabled={isVerifying}
                        onClick={onBack}
                    >
                        {t('mfaBackToLogin')}
                    </Button>
                </Box>
            )}
        </form>
    );
};
